import type { DraftShadowTopicRow } from '@sbc/domain-models/gateway';
import { useNavigate } from '@tanstack/react-router';

import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDraftShadowReport } from '@/lib/api/draft-shadow';
import { share } from '@/lib/utils';

import { AutoReplyPanel } from './auto-reply-panel';

export const AUTO_SEND_BAR = 0.95;
export const MIN_REVIEWED_FOR_READY = 20;
const PERIODS = [7, 14, 30, 90] as const;
const DEFAULT_DAYS = 14;

export interface DraftShadowSearch {
  days?: number;
}

export function parseDraftShadowSearch(search: Record<string, unknown>): DraftShadowSearch {
  const days = Number(search.days);
  return { days: PERIODS.some((period) => period === days) ? days : undefined };
}

export function isReadyToAutoSend(row: DraftShadowTopicRow): boolean {
  return (
    row.wouldSendReviewed >= MIN_REVIEWED_FOR_READY &&
    row.wouldSendRight / row.wouldSendReviewed >= AUTO_SEND_BAR
  );
}

export function DraftShadowPage({ search }: { search: DraftShadowSearch }) {
  const navigate = useNavigate();
  const days = search.days ?? DEFAULT_DAYS;
  const reportQuery = useDraftShadowReport(days);
  const report = reportQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Draft shadow</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Each Intercom draft checked against the reply CS sent. A topic is ready to try auto-send
            when at least {Math.round(AUTO_SEND_BAR * 100)}% of its would-send drafts were right,
            over {MIN_REVIEWED_FOR_READY} or more reviewed.
          </p>
        </div>
        <Select
          value={String(days)}
          onValueChange={(value) =>
            void navigate({ to: '/gateway/draft-shadow', search: { days: Number(value) } })
          }
        >
          <SelectTrigger aria-label="Period" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((period) => (
              <SelectItem key={period} value={String(period)}>
                Last {period} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AutoReplyPanel days={days} />

      <h2 className="text-lg font-semibold tracking-tight">Shadow review</h2>

      {reportQuery.isPending && <p className="text-sm">Loading…</p>}
      {reportQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load the draft shadow report: {reportQuery.error.message}
        </p>
      )}

      {report && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Topic</TableHead>
                  <TableHead className="text-right">Drafts</TableHead>
                  <TableHead className="text-right">Would send</TableHead>
                  <TableHead className="text-right">Would-send right</TableHead>
                  <TableHead className="text-right">All right</TableHead>
                  <TableHead className="text-right">Sent as-is</TableHead>
                  <TableHead className="text-right">Waiting for CS</TableHead>
                  <TableHead className="text-right">No CS reply</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.topics.length ? (
                  report.topics.map((row) => (
                    <TableRow key={row.topic}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {row.topic}
                          {isReadyToAutoSend(row) && <Badge>Ready to try</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.drafts}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.wouldSend} ({share(row.wouldSend, row.drafts)})
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {share(row.wouldSendRight, row.wouldSendReviewed)}
                        <span className="text-xs text-[var(--color-muted-foreground)]">
                          {' '}
                          of {row.wouldSendReviewed}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {share(row.judgedRight, row.reviewed)}
                        <span className="text-xs text-[var(--color-muted-foreground)]">
                          {' '}
                          of {row.reviewed}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {share(row.sentAsIs, row.reviewed)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.awaitingReply}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.noReply}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      No drafts in this period yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-2">
            <h2 className="text-lg font-semibold tracking-tight">Drafts judged wrong</h2>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Drafted</TableHead>
                    <TableHead>Topic</TableHead>
                    <TableHead>Would send</TableHead>
                    <TableHead>CS reply</TableHead>
                    <TableHead className="w-1/2">Why it was wrong</TableHead>
                    <TableHead>Conversation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.wrongDrafts.length ? (
                    report.wrongDrafts.map((wrong) => (
                      <TableRow key={wrong.id}>
                        <TableCell className="text-sm">
                          {new Date(wrong.draftedAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm">{wrong.topic}</TableCell>
                        <TableCell>
                          {wrong.wouldSend ? (
                            <Badge variant="destructive">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{wrong.use ?? '—'}</TableCell>
                        <TableCell className="text-sm">{wrong.reason}</TableCell>
                        <TableCell>
                          <a
                            href={wrong.conversationUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-medium hover:underline"
                          >
                            Open in Intercom
                          </a>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center">
                        No draft was judged wrong in this period.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
